import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { normaliseRoomCode } from '@crew/shared';
import type {
  ClientSnapshot,
  ClientToServerEvents,
  Credentials,
  ServerToClientEvents
} from '@crew/shared';

const STORAGE_KEY = 'crew-online:credentials';
const SERVER_URL = import.meta.env.VITE_SERVER_URL as string | undefined;

type CrewSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

function loadCredentials(): Credentials | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Credentials>;
    if (!parsed.roomCode || !parsed.playerId || !parsed.token) return null;
    return { roomCode: parsed.roomCode, playerId: parsed.playerId, token: parsed.token };
  } catch {
    return null;
  }
}

function saveCredentials(credentials: Credentials | null): void {
  try {
    if (credentials) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private browsing - sessions just will not resume */
  }
}

export type ConnectionState = 'connecting' | 'online' | 'offline';

export interface Crew {
  connection: ConnectionState;
  snapshot: ClientSnapshot | null;
  playerId: string | null;
  error: string | null;
  clearError: () => void;
  createRoom: (name: string, avatar: string, avatarColor?: string) => Promise<void>;
  joinRoom: (
    roomCode: string,
    name: string,
    avatar: string,
    avatarColor?: string
  ) => Promise<void>;
  leaveRoom: () => Promise<void>;
  setMission: (missionId: number) => Promise<void>;
  setAvatar: (avatar: string, avatarColor: string, mood?: string) => Promise<void>;
  setHouseRules: (patch: { relaxedComms?: boolean; hideFlightLog?: boolean }) => Promise<void>;
  start: () => Promise<void>;
  restart: (nextMission: boolean) => Promise<void>;
  abort: () => Promise<void>;
  pickTask: (taskId: string) => Promise<void>;
  endDraftTurn: () => Promise<void>;
  autoAssignTasks: () => Promise<void>;
  playCard: (cardId: string) => Promise<boolean>;
  communicate: (cardId: string, hint: 'highest' | 'lowest' | 'only' | 'have') => Promise<void>;
  sendChat: (text: string) => Promise<void>;
  poke: (targetId: string) => Promise<void>;
  /** Bumped each time a poke lands, so the table can animate it. */
  lastPoke: { fromId: string; toId: string; at: number } | null;
  /** Resolves true when the server accepted it, so the dialog can confirm. */
  sendFeedback: (input: {
    category: string;
    message: string;
    screen: string;
    missionId?: number;
    phase?: string;
  }) => Promise<boolean>;
}

export function useCrew(): Crew {
  const socketRef = useRef<CrewSocket | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [snapshot, setSnapshot] = useState<ClientSnapshot | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(loadCredentials()?.playerId ?? null);
  const [error, setError] = useState<string | null>(null);
  const [lastPoke, setLastPoke] = useState<{ fromId: string; toId: string; at: number } | null>(
    null
  );

  useEffect(() => {
    const socket: CrewSocket = SERVER_URL ? io(SERVER_URL) : io();
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnection('online');
      const saved = loadCredentials();
      if (!saved) return;
      // An explicit invite takes precedence over an unrelated remembered seat.
      // Keep its credentials until a new join succeeds, so a bad invite is recoverable.
      const invitedRoom = normaliseRoomCode(new URLSearchParams(window.location.search).get('room') ?? '');
      if (invitedRoom && invitedRoom !== saved.roomCode) {
        setPlayerId(null);
        setSnapshot(null);
        return;
      }
      socket.emit('room:resume', saved, (result) => {
        if (result.ok) {
          setPlayerId(result.data.playerId);
        } else {
          saveCredentials(null);
          setPlayerId(null);
          setSnapshot(null);
        }
      });
    });

    socket.on('disconnect', () => setConnection('offline'));
    socket.on('connect_error', () => setConnection('offline'));
    socket.on('snapshot', setSnapshot);
    socket.on('toast', ({ text }) => setError(text));
    socket.on('poked', ({ fromId, toId }) => setLastPoke({ fromId, toId, at: Date.now() }));
    socket.on('kicked', ({ reason }) => {
      saveCredentials(null);
      setPlayerId(null);
      setSnapshot(null);
      setError(reason);
    });

    return () => {
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, []);

  const request = useCallback(
    <E extends keyof ClientToServerEvents, T>(event: E, payload: unknown): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket || !socket.connected) {
          reject(new Error('Not connected to the server.'));
          return;
        }
        const timer = window.setTimeout(() => reject(new Error('The server did not answer.')), 10_000);
        // The typed event map guarantees the ack shape at the call sites below.
        (socket.emit as unknown as (e: string, p: unknown, ack: (r: unknown) => void) => void)(
          event as string,
          payload,
          (raw: unknown) => {
            window.clearTimeout(timer);
            const result = raw as { ok: true; data: T } | { ok: false; error: string };
            if (result?.ok) resolve(result.data);
            else reject(new Error(result?.error ?? 'Unknown error.'));
          }
        );
      }),
    []
  );

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        setError(null);
        await fn();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong.');
      }
    },
    []
  );

  const adopt = useCallback((credentials: Credentials) => {
    saveCredentials(credentials);
    setPlayerId(credentials.playerId);
    const url = new URL(window.location.href);
    url.searchParams.set('room', credentials.roomCode);
    window.history.replaceState(null, '', url);
  }, []);

  return useMemo<Crew>(
    () => ({
      connection,
      snapshot,
      playerId,
      error,
      lastPoke,
      clearError: () => setError(null),
      createRoom: (name, avatar, avatarColor) =>
        run(async () =>
          adopt(
            await request<'room:create', Credentials>('room:create', { name, avatar, avatarColor })
          )
        ),
      joinRoom: (roomCode, name, avatar, avatarColor) =>
        run(async () =>
          adopt(
            await request<'room:join', Credentials>('room:join', {
              roomCode,
              name,
              avatar,
              avatarColor
            })
          )
        ),
      leaveRoom: () =>
        run(async () => {
          await request('room:leave', {});
          saveCredentials(null);
          setPlayerId(null);
          setSnapshot(null);
          const url = new URL(window.location.href);
          url.searchParams.delete('room');
          window.history.replaceState(null, '', url);
        }),
      setMission: (missionId) => run(() => request('room:setMission', { missionId })),
      setAvatar: (avatar, avatarColor, mood) =>
        run(() => request('room:setAvatar', { avatar, avatarColor, mood })),
      setHouseRules: (patch) => run(() => request('room:setHouseRules', patch)),
      start: () => run(() => request('game:start', {})),
      restart: (nextMission) => run(() => request('game:restart', { nextMission })),
      abort: () => run(() => request('game:abort', {})),
      pickTask: (taskId) => run(() => request('game:pickTask', { taskId })),
      endDraftTurn: () => run(() => request('game:endDraftTurn', {})),
      autoAssignTasks: () => run(() => request('game:autoAssignTasks', {})),
      playCard: async (cardId) => {
        try {
          setError(null);
          await request('game:playCard', { cardId });
          return true;
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Could not play that card.');
          return false;
        }
      },
      communicate: (cardId, hint) => run(() => request('game:communicate', { cardId, hint })),
      sendChat: (text) => run(() => request('chat:send', { text })),
      poke: (targetId) => run(() => request('player:poke', { targetId })),
      sendFeedback: async (input) => {
        try {
          setError(null);
          await request('feedback:send', input);
          return true;
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Could not send that.');
          return false;
        }
      }
    }),
    [adopt, connection, error, lastPoke, playerId, request, run, snapshot]
  );
}
